/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import {
	ISchemaOneOfModel,
	SCHEMA_VALUE_TYPE,
	TGenericModelNode,
	TSchemaOneOfTypes
} from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { InspectModel } from "../InspectModel";
import { FieldSectionContent } from "../../Fields/FieldSectionContent";

function renderOneOfValue(
	model: ISchemaOneOfModel<TSchemaOneOfTypes>,
	type: string,
	path: string[],
	depth: number
): JSX.Element {
	if (!model.value[type]) {
		// @todo return error
		return <p>Type {type} not found.</p>;
	}

	return <InspectModel model={model.value[type]} path={path.concat([ "type_" + type ])} depth={depth} />;
}

export const ControlOneOf_Children: TInspectorControlComponent<ISchemaOneOfModel<TSchemaOneOfTypes>> = ({
	model: _model,
	path,
	depth
}) => {
	const { model } = useBlueprintModel(_model, "oneOf");
	const { model: typeModel } = useBlueprintModel(_model?.type);
	const { model: typeConstModel } = useBlueprintModel(_model?.type?.constant);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	const schema = model.schema;

	return (
		<>
			<InspectModel model={typeModel} path={path.concat([ "type" ])} depth={depth} />
			{typeModel.type === SCHEMA_VALUE_TYPE.CONST ? (
				<FieldSectionContent padded>
					{renderOneOfValue(model, typeConstModel.value, path, depth + 1)}
				</FieldSectionContent>
			) : (
				Object.keys(schema.opts.types).map((type) => (
					<FieldSectionContent padded>
						{renderOneOfValue(model, type, path, depth + 1)}
					</FieldSectionContent>
				))
			)}
		</>
	);
};

export const ControlOneOf = (model: TGenericModelNode): IInspectorControls => ({
	children: {
		component: ControlOneOf_Children,
		model: model
	}
});
